import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import io from 'socket.io-client';
import IconComponents from '../components/icons';

const DEFAULT_IMAGE = "/images/serverboost.gif";

const Home = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchParams] = useSearchParams();
  const selectedCategory = searchParams.get('category');

  useEffect(() => {
    // Charger les produits depuis l'API
    fetchProducts();
    fetchCategories();

    // Connexion Socket.IO
    const socket = io(process.env.REACT_APP_API_URL);
    
    // Écouter les mises à jour de produits
    socket.on('product_updated', (updatedProduct) => {
      setProducts(prevProducts => 
        prevProducts.map(p => p.id === updatedProduct.id ? updatedProduct : p)
      );
    });

    // Écouter les suppressions de produits
    socket.on('product_deleted', (deletedProductId) => {
      setProducts(prevProducts => 
        prevProducts.filter(p => p.id !== deletedProductId)
      );
    });

    // Écouter la réorganisation des produits
    socket.on('products_reordered', (newProducts) => {
      setProducts(newProducts);
    });

    return () => socket.disconnect();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/products`);
      const allProducts = await response.json();
      setProducts(allProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/categories`);
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const renderIcon = (iconName, color = 'text-[#22c55e]') => {
    const Icon = IconComponents[iconName];
    return Icon ? (
      <div className={`w-6 h-6 ${color}`}>
        <Icon />
      </div>
    ) : null;
  };

  // Filtrer les produits en fonction de la catégorie sélectionnée
  const filteredProducts = selectedCategory
    ? products.filter(p => p.type === selectedCategory)
    : products;

  // Fonction pour obtenir le titre de la section en fonction de la catégorie
  const getCategoryTitle = () => {
    if (!selectedCategory) return 'All Products';
    const category = categories.find(c => c.id === selectedCategory);
    return category ? category.name : 'All Products';
  };

  const scrollToProducts = (e) => {
    e.preventDefault();
    const productsSection = document.getElementById('products');
    if (productsSection) {
      productsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="relative py-14">
        <div className="mx-auto max-w-7xl px-6">
          <div>
            <h1 className="text-[#22c55e] text-xl font-medium mb-2">
              Welcome to OLYMP
            </h1>
            <div className="text-[44px] font-bold text-white leading-tight mb-5">
            Digital Subscriptions & Discord Services
            </div>
            <p className="text-lg leading-relaxed text-gray-300 mb-6 max-w-3xl">
              Elevate your Discord experience with OLYMP! We provide premium Discord services and digital upgrades at the best prices. From server boosts to account upgrades, enjoy our 100% secure and reliable solutions with instant support.
            </p>
            <div className="mb-10">
              <button
                onClick={scrollToProducts}
                className="inline-block rounded-md bg-[#22c55e] px-7 py-2.5 text-base font-semibold text-black shadow-sm hover:bg-[#15803d] transition-all duration-300"
              >
                Browse products
              </button>
            </div>

            {/* Stats Section */}
            <div className="flex space-x-14">
              <div>
                <div className="text-[#22c55e] text-3xl font-bold">22+</div>
                <div className="text-gray-500 text-sm mt-1">Products</div>
              </div>
              <div>
                <div className="text-[#22c55e] text-3xl font-bold">5.0 / 5.0</div>
                <div className="text-gray-500 text-sm mt-1">Reputation</div>
              </div>
              <div>
                <div className="text-[#22c55e] text-3xl font-bold">2000+</div>
                <div className="text-gray-500 text-sm mt-1">Customers</div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Cards Section */}
        <div className="mx-auto max-w-7xl px-6 mt-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {/* Safe and Legal Card */}
            <div className="group relative overflow-hidden rounded-lg bg-[#0f1f0f]/50 p-8 transition-all duration-500 hover:bg-[#22c55e]/10 border border-[#22c55e]/10 hover:border-[#22c55e]/30">
              <div className="relative z-10">
                <div className="mb-5 inline-flex rounded-lg bg-[#22c55e]/20 p-3">
                  <svg className="h-7 w-7 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="mb-3 text-xl font-semibold text-white group-hover:text-[#22c55e] transition-colors duration-300">
                  Safe and Legal
                </h3>
                <p className="text-base text-gray-300 group-hover:text-gray-200 transition-colors duration-300">
                  Legally paid upgrades with regional pricing ensure security of your accounts. 0% kick rate and warranty offer peace of mind against stolen or hacked accounts.
                </p>
              </div>
            </div>

            {/* Quick Support Card */}
            <div className="group relative overflow-hidden rounded-lg bg-[#0f1f0f]/50 p-8 transition-all duration-500 hover:bg-[#22c55e]/10 border border-[#22c55e]/10 hover:border-[#22c55e]/30">
              <div className="relative z-10">
                <div className="mb-5 inline-flex rounded-lg bg-[#22c55e]/20 p-3">
                  <svg className="h-7 w-7 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                  </svg>
                </div>
                <h3 className="mb-3 text-xl font-semibold text-white group-hover:text-[#22c55e] transition-colors duration-300">
                  Quick Support
                </h3>
                <p className="text-base text-gray-300 group-hover:text-gray-200 transition-colors duration-300">
                  Experience lightning-fast support! Our dedicated team is committed to providing quick and efficient responses to all your questions.
                </p>
              </div>
            </div>

            {/* Payment Flexibility Card */}
            <div className="group relative overflow-hidden rounded-lg bg-[#0f1f0f]/50 p-8 transition-all duration-500 hover:bg-[#22c55e]/10 border border-[#22c55e]/10 hover:border-[#22c55e]/30">
              <div className="relative z-10">
                <div className="mb-5 inline-flex rounded-lg bg-[#22c55e]/20 p-3">
                  <svg className="h-7 w-7 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <h3 className="mb-3 text-xl font-semibold text-white group-hover:text-[#22c55e] transition-colors duration-300">
                  Payment Flexibility
                </h3>
                <p className="text-base text-gray-300 group-hover:text-gray-200 transition-colors duration-300">
                  Choose the payment method that suits you best. We offer a variety of convenient options, including PayPal, Revolut, Bank transfer and Crypto.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Delivery Info Section */}
        <div className="mx-auto max-w-7xl px-6 mt-16">
          <div className="relative overflow-hidden rounded-lg bg-[#22c55e]/5 border border-[#22c55e]/20 p-6">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#22c55e] mb-2">Instant Automatic Delivery</h3>
                <p className="text-gray-300">
                  Experience the convenience of our automated delivery system! All products in this section are delivered instantly after payment confirmation - no waiting, no manual intervention needed. Your items will be automatically sent to you within seconds.
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Categories Navigation */}
      <div className="mt-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex overflow-x-auto scrolling-touch gap-4 pb-4 hide-scrollbar">
            {categories.map(category => {
              const isSelected = searchParams.get('category') === category.id;
              return (
                <Link 
                  key={category.id}
                  to={isSelected ? '/' : `/?category=${category.id}`}
                  className={`category-card group relative overflow-hidden rounded-lg transition-all duration-500 ease-in-out flex-shrink-0 min-w-[180px] ${
                    isSelected 
                      ? 'bg-[#22c55e]/5 border border-[#22c55e]/20 backdrop-blur-sm' 
                      : 'bg-black/10 hover:bg-black/20 border border-white/5 backdrop-blur-sm hover:border-[#22c55e]/15'
                  }`}
                >
                  <div className="relative p-4 flex items-center w-full">
                    <div className="flex items-center gap-3">
                      <div className={`flex-shrink-0 transition-all duration-300 ease-in-out ${
                        isSelected ? 'text-[#22c55e]' : 'text-[#22c55e]/80'
                      }`}>
                        {renderIcon(category.icon, `${
                          category.id === 'reactions' ? 'w-6 h-6' : 'w-5 h-5'
                        } transition-transform duration-300 ${
                          isSelected ? 'transform scale-110' : 'group-hover:scale-105'
                        }`)}
                      </div>
                      <h3 className={`text-base font-medium transition-all duration-300 ease-in-out ${
                        isSelected 
                          ? 'text-[#22c55e]' 
                          : 'text-gray-400 group-hover:text-gray-200'
                      }`}>
                        {category.name}
                      </h3>
                    </div>
                  </div>
                  <div className={`absolute inset-0 bg-gradient-to-r from-transparent ${
                    isSelected ? 'to-[#22c55e]/5' : 'to-transparent'
                  } opacity-0 group-hover:opacity-100 transition-all duration-500 ease-in-out`}></div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Products Section */}
      <div id="products" className="max-w-7xl mx-auto px-6 py-16">
        <section>
          <h2 className="text-2xl font-bold mb-6">{getCategoryTitle()}</h2>
          {filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {filteredProducts.map(product => (
                <ProductCard 
                  key={product.id} 
                  {...product} 
                  image={product.image || DEFAULT_IMAGE}
                  deliveryType={product.deliveryType || 'serials'}
                />
              ))}
            </div>
          ) : (
            <div className="text-left py-12">
              <h2 className="text-2xl font-bold text-gray-400">No products available</h2>
              <p className="text-gray-500 mt-2">Please check back later</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Home; 